import React, { ReactNode, useState } from "react";
import { Search, SlidersHorizontal, X, ArrowUpDown, Filter, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface SortOption {
  value: string;
  label: string;
}

interface FilterOption {
  id: string;
  label: string;
  options: { value: string; label: string }[];
}

interface FilterToolbarProps {
  // Search
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  // Sort
  sortOptions?: SortOption[];
  sortValue?: string;
  onSortChange?: (value: string) => void;
  sortDir?: "asc" | "desc";
  onSortDirChange?: (dir: "asc" | "desc") => void;

  // Filters
  filters?: FilterOption[];
  filterValues?: Record<string, string>;
  onFilterChange?: (filterId: string, value: string) => void;
  onClearFilters?: () => void;

  // Advanced filter builder support
  advancedFilter?: ReactNode;
  hasAdvancedFilters?: boolean;
  advancedFilterCount?: number;
  onClearAdvancedFilters?: () => void;

  // Actions
  actions?: ReactNode;

  // Optional leading slot rendered as the first child of the sort/filters row (row 2 col 1)
  leadingFilterAction?: ReactNode;

  // Layout
  collapsibleFilters?: boolean;
  className?: string;
}

const FilterToolbar = ({
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  sortOptions,
  sortValue,
  onSortChange,
  sortDir,
  onSortDirChange,
  filters,
  filterValues = {},
  onFilterChange,
  onClearFilters,
  advancedFilter,
  hasAdvancedFilters = false,
  advancedFilterCount = 0,
  onClearAdvancedFilters,
  actions,
  leadingFilterAction,
  collapsibleFilters = true,
  className,
}: FilterToolbarProps) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const activeFilterCount = Object.values(filterValues).filter(
    (v) => v && v !== "all"
  ).length;
  
  const totalActiveFilters = activeFilterCount + advancedFilterCount;
  const hasFilters = filters && filters.length > 0;
  const hasAdvancedFilterSlot = !!advancedFilter;
  const showFilterToggle = (hasFilters || hasAdvancedFilterSlot) && collapsibleFilters;

  const FilterContent = () => (
    <div className="flex flex-wrap gap-3">
      {filters?.map((filter) => (
        <div key={filter.id} className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground font-medium">
            {filter.label}
          </label>
          <Select
            value={filterValues[filter.id] || "all"}
            onValueChange={(value) => onFilterChange?.(filter.id, value)}
          >
            <SelectTrigger className="w-[150px] h-9">
              <SelectValue placeholder={`All ${filter.label}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {filter.options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
      {activeFilterCount > 0 && onClearFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="self-end h-9 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4 mr-1" />
          Clear filters
        </Button>
      )}
      {hasAdvancedFilters && onClearAdvancedFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAdvancedFilters}
          className="self-end h-9 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4 mr-1" />
          Clear advanced
        </Button>
      )}
    </div>
  );

  return (
    <div className={cn("space-y-2", className)}>
      {/* Grid layout for aligned rows */}
      {(onSearchChange || actions || sortOptions || showFilterToggle || leadingFilterAction) && (
      <div className="grid grid-cols-[minmax(0,1fr)_6rem] sm:grid-cols-[minmax(0,1fr)_auto] gap-2">
          {/* Row 1, Col 1: Search */}
          {onSearchChange ? (
            <div className="relative min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-9 h-9"
              />
              {searchValue && (
                <button
                  onClick={() => onSearchChange("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : <div />}

          {/* Row 1, Col 2: Actions */}
          {actions ? (
            <div className="flex gap-2 [&>*]:w-full overflow-hidden">{actions}</div>
          ) : <div />}

          {/* Row 2, Col 1: Leading slot + Sort select + Filters button */}
          {(sortOptions || showFilterToggle || leadingFilterAction) ? (
            <div className="flex gap-2 min-w-0">
              {leadingFilterAction}
              {sortOptions && sortOptions.length > 0 && (
                <Select value={sortValue} onValueChange={onSortChange}>
                  <SelectTrigger className="flex-1 min-w-0 h-9">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {showFilterToggle && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                  className="h-9 shrink-0 justify-center gap-1.5 min-w-[5.5rem]"
                >
                  <Filter className="h-4 w-4 shrink-0" />
                  <span>Filters</span>
                  {totalActiveFilters > 0 && (
                    <Badge variant="secondary" className="h-5 px-1.5">
                      {totalActiveFilters}
                    </Badge>
                  )}
                </Button>
              )}
            </div>
          ) : <div />}

          {/* Row 2, Col 2: Asc/Desc button */}
          {(sortOptions || showFilterToggle || leadingFilterAction) ? (
            onSortDirChange && sortDir ? (
              <div className="flex gap-2 [&>*]:w-full overflow-hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSortDirChange(sortDir === "asc" ? "desc" : "asc")}
                  className="flex items-center justify-center gap-1.5 h-9"
                >
                  <ArrowUpDown className="h-4 w-4 shrink-0" />
                  <span className="truncate">{sortDir === "asc" ? "Asc" : "Desc"}</span>
                </Button>
              </div>
            ) : <div />
          ) : null}
        </div>
      )}

      {/* Active filters banner */}
      {hasAdvancedFilters && !isFiltersOpen && showFilterToggle && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 py-2 px-3 space-y-1">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
            <span className="text-sm text-foreground">
              Showing filtered results{advancedFilterCount > 0 && ` (${advancedFilterCount} active ${advancedFilterCount === 1 ? 'filter' : 'filters'})`}
            </span>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFiltersOpen(true)}
              className="h-7 text-xs"
            >
              Show Filters
            </Button>
            {onClearAdvancedFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAdvancedFilters}
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Collapsible filter panel */}
      {showFilterToggle && (
        <Collapsible open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
          <CollapsibleContent className="pt-2 border-t space-y-4">
            {hasFilters && <FilterContent />}
            {advancedFilter && (
              <div className="pt-2">
                {advancedFilter}
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Inline filters (when not collapsible) */}
      {hasFilters && !collapsibleFilters && <FilterContent />}
      
      {/* Inline advanced filter (when not collapsible) */}
      {advancedFilter && !collapsibleFilters && (
        <div className="pt-2">
          {advancedFilter}
        </div>
      )}

      {/* Active filter badges */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(filterValues).map(([filterId, value]) => {
            if (!value || value === "all") return null;
            const filter = filters?.find((f) => f.id === filterId);
            const option = filter?.options.find((o) => o.value === value);
            if (!filter || !option) return null;

            return (
              <Badge
                key={filterId}
                variant="secondary"
                className="gap-1 pr-1"
              >
                {filter.label}: {option.label}
                <button
                  onClick={() => onFilterChange?.(filterId, "all")}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FilterToolbar;
